<template>
  <!-- Dropdown popup positioned relative to filter button -->
  <div v-if="visible" class="absolute right-0 z-50 p-3 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg filter-popup top-full" data-filter-modal>
    <!-- Header -->
    <div class="flex items-center justify-between mb-3">
      <h3 class="text-lg font-normal text-gray-500">Filter</h3>
      <button
        @click="onCancel"
        class="text-gray-400 hover:text-gray-600 focus:outline-none"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </button>
    </div>

    <!-- Filter content -->
    <div class="">
      <div class="grid grid-cols-2 gap-6">
        <!-- Vendor -->
        <div>
          <FormLabel htmlFor="vendor">Publisher</FormLabel>

          <!-- Vendor input field with chips inside -->
          <div class="relative" ref="vendorDropdownContainer">
            <div
              @click="showVendorDropdown = !showVendorDropdown"
              class="w-full px-3 py-1 border border-gray-300 rounded-md vendor-input bg-gray-50 cursor-text"
            >
              <!-- Selected vendors as chips inside input -->
              <div class="flex flex-wrap gap-2">
                <div
                  v-for="vendor in selectedVendors"
                  :key="vendor.id"
                  class="flex items-center px-2 py-1 text-sm text-blue-800 bg-blue-100 rounded"
                >
                  {{ vendor.name }}
                  <button
                    @click.stop="removeVendor(vendor)"
                    class="ml-1 text-blue-600 hover:text-blue-800 focus:outline-none"
                  >
                    <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </button>
                </div>

                <!-- Placeholder text when no vendors selected -->
                <div v-if="selectedVendors.length === 0" class="py-1 text-gray-500 text-base">
                  Select Publisher
                </div>
              </div>
            </div>

            <!-- Dropdown arrow -->
            <ChevronDownFilled className="absolute w-3 h-3 text-gray-500 transform -translate-y-1/2 pointer-events-none right-3 top-1/2" />

            <!-- Vendor dropdown menu -->
            <div
              v-if="showVendorDropdown"
              class="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg vendor-dropdown"
            >
              <!-- Search input -->
              <div class="p-2 border-b">
                <div class="relative">
                  <SearchOutline className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    v-model="vendorSearchTerm"
                    @input="handleVendorSearch"
                    type="text"
                    placeholder="Search Publisher"
                    class="w-full py-2 pr-3 border border-gray-300 rounded-md pl-9 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <!-- Vendor list -->
              <div class="overflow-y-auto vendor-list-container">
                <div
                  v-for="vendor in vendorOptions"
                  :key="vendor.id"
                  @click="toggleVendor(vendor)"
                  class="flex items-center px-3 py-2 cursor-pointer hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    :checked="isVendorSelected(vendor)"
                    class="mr-2"
                    readonly
                  />
                  {{ vendor.name }}
                </div>
              </div>

              <div v-if="isLoadingVendors" class="p-3 text-center text-gray-500">
                Loading publishers...
              </div>
            </div>
          </div>
        </div>

        <!-- Role -->
        <div>
          <FormLabel htmlFor="role">Role</FormLabel>
          <SelectRadio
            id="role"
            defaultText="Select Role"
            v-model="filters.role"
            :options="roleOptions"
          />
        </div>

        <!-- Group Type -->
        <div>
          <FormLabel htmlFor="group">Group Type</FormLabel>
          <SelectRadio
            id="group"
            defaultText="Select Group Type"
            v-model="filters.group"
            :options="groupOptions"
          />
        </div>

        <!-- Status -->
        <div>
          <FormLabel htmlFor="status">Status</FormLabel>
          <SelectRadio
            id="status"
            defaultText="Select Status"
            v-model="filters.status"
            :options="statusOptions"
          />
        </div>
      </div>

      <!-- Action buttons -->
      <div class="flex justify-start gap-3 mt-6">
        <Button
          @click="onApplyFilter"
          color="primary"
          size="base"
          class="px-6 py-2"
        >
          Filter
        </Button>

        <Button
          @click="onReset"
          color="alternative"
          variant="outlined"
          size="base"
          class="px-6 py-2"
        >
          Reset
        </Button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import Button from '@/components/ui/Button';
import FormLabel from '@/components/ui/FormLabel';
import SelectRadio from '@/components/ui/SelectRadio';
import SearchOutline from '@/components/icons/SearchOutline';
import ChevronDownFilled from '@/components/icons/ChevronDownFilled';

export default {
  name: 'AdminFilterModal',
  components: {
    Button,
    FormLabel,
    SelectRadio,
    SearchOutline,
    ChevronDownFilled
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    initialFilters: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      filters: {
        role: null,
        group: null,
        status: null
      },
      selectedVendors: [],
      vendorOptions: [],
      vendorSearchTerm: '',
      showVendorDropdown: false,
      isLoadingVendors: false,
      userData: JSON.parse(localStorage.getItem('session_auth')),
      // Dynamic options
      roleOptions: [],
      isLoadingRoles: false,
      groupOptions: [
        { value: '', text: 'All Group Type' },
        { value: 'ebook', text: 'Ebook' },
        { value: 'publisher', text: 'Publisher' },
          { value: 'publisher_group', text: 'Publisher Group' }
      ],
      statusOptions: [
        { value: '', text: 'All Status' },
        { value: true, text: 'Active' },
        { value: false, text: 'Inactive' }
      ]
    };
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.initializeFilters();
        this.loadInitialVendors();
        // Add click outside listener when modal opens
        this.$nextTick(() => {
          document.addEventListener('click', this.handleClickOutside);
        });
      } else {
        // Remove click outside listener when modal closes
        document.removeEventListener('click', this.handleClickOutside);
      }
    },
    showVendorDropdown(newVal) {
      if (newVal && this.vendorOptions.length === 0) {
        this.loadInitialVendors();
      }
    }
  },
  methods: {
    ...mapActions('vendors', ['findVendor']),
    ...mapActions('accessRole', ['fetchAllRole']),

    initializeFilters() {
      this.filters = { ...this.filters, ...this.initialFilters };
      if (this.initialFilters.vendors) {
        this.selectedVendors = [...this.initialFilters.vendors];
      }
      this.loadRoles();
    },

    async loadRoles() {
      if (this.isLoadingRoles || this.roleOptions.length > 1) return;

      this.isLoadingRoles = true;
      try {
        const res = await this.fetchAllRole();
        const isRole14 = this.userData.role_id === 14;

    const filteredRoles = isRole14
      ? res.filter(item => item.id !== 1)
      : res;

     this.roleOptions = [
      { value: '', text: 'All Roles' },
      ...filteredRoles.map(item => ({
        value: item.name,
        text: item.name.replace(/\b\w/g, l => l.toUpperCase())
      }))
    ];
    
      } catch (error) {
        console.error('Error loading roles:', error);
      } finally {
        this.isLoadingRoles = false;
      }
    },

    async loadInitialVendors() {
      if (this.isLoadingVendors) return;

      this.isLoadingVendors = true;
      try {
        const response = await this.findVendor({ q: '' });
        this.vendorOptions = response.data?.data || [];
      } catch (error) {
        console.error('Error loading vendors:', error);
      } finally {
        this.isLoadingVendors = false;
      }
    },

    async handleVendorSearch() {
      if (this.vendorSearchTerm.length < 2) {
        this.loadInitialVendors();
        return;
      }

      this.isLoadingVendors = true;
      try {
        const response = await this.findVendor({ q: this.vendorSearchTerm });
        this.vendorOptions = response.data?.data || [];
      } catch (error) {
        console.error('Error searching vendors:', error);
      } finally {
        this.isLoadingVendors = false;
      }
    },

    toggleVendor(vendor) {
      const index = this.selectedVendors.findIndex(v => v.id === vendor.id);
      if (index > -1) {
        this.selectedVendors.splice(index, 1);
      } else {
        this.selectedVendors.push(vendor);
      }
    },

    removeVendor(vendor) {
      const index = this.selectedVendors.findIndex(v => v.id === vendor.id);
      if (index > -1) {
        this.selectedVendors.splice(index, 1);
      }
    },

    isVendorSelected(vendor) {
      return this.selectedVendors.some(v => v.id === vendor.id);
    },

    onApplyFilter() {
      const filterData = {
        vendors: this.selectedVendors,
        role: this.filters.role,
        group: this.filters.group,
        status: this.filters.status
      };

      this.$emit('apply-filter', filterData);
      this.$emit('update:visible', false);
    },

    onReset() {
      this.filters = {
        role: null,
        group: null,
        status: null
      };
      this.selectedVendors = [];
      this.vendorSearchTerm = '';

      this.$emit('reset-filter');
      this.$emit('update:visible', false);
    },

    onCancel() {
      this.$emit('update:visible', false);
    },

    handleClickOutside(event) {
      // Close vendor dropdown if clicking outside of it
      if (this.showVendorDropdown && this.$refs.vendorDropdownContainer && !this.$refs.vendorDropdownContainer.contains(event.target)) {
        this.showVendorDropdown = false;
      }
    }
  },

  beforeDestroy() {
    // Clean up event listener
    document.removeEventListener('click', this.handleClickOutside);
  }
};
</script>

<style scoped>
/* Filter popup width */
.filter-popup {
  width: 600px;
}

/* Vendor input styling */
.vendor-input {
  min-height: 42px;
}

.vendor-input:focus-within {
  outline: none;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5);
  border-color: #3b82f6;
}

/* Vendor dropdown styling */
.vendor-dropdown {
  max-height: 300px;
  display: flex;
  flex-direction: column;
}

.vendor-list-container {
  max-height: 200px;
  overflow-y: auto;
}

/* Custom scrollbar styling */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
